import React from 'react';
import { Redirect } from 'react-router-dom';
import {
  Footer as WixFooter,
  Header as WixHeader,
  types as HeaderFooterTypes,
} from '@wix/marketing-header-and-footer-react';
import { ExperimentsStore } from '../../../stores/ExperimentsStore';
import { RedirectStore } from '../../stores/RedirectStore';
import { UiLoggerStore } from '../../stores/UiLoggerStore';
import { ConfigStore } from '../../stores/ConfigStore';
import { TemplatesStore } from '../../stores/TemplatesStore';
import { ForYouPageStore } from '../../stores/ForYouPageStore';
import { RoutingStore } from '../../stores/RoutingStore';
import { isForYouPageRouteLoaded } from '../../stores/routes/route';
import { injectV2 } from '../../injectV2';
import { HtmlHead } from '../HtmlHead/HtmlHead';
import { HeaderTitle } from '../HeaderTitle/HeaderTitle';
import { HeaderMenuPanel } from '../HeaderMenuPanel/HeaderMenuPanel';
import { HeaderMenuPanelForYou } from '../HeaderMenuPanel/HeaderMenuPanelForYou';
import { DealerBanner } from '../DealerBanner/DealerBanner';
import { ForYouPage } from '../ForYouPage/ForYouPage';
import { TemplatesPage } from '../TemplatesPage/TemplatesPage';
import { ScrollToTopContext } from '../../contexts/ScrollToTopContext';
import '../../styles/global.scss';
import { appDataHooks } from './App.dataHooks';
import styles from './App.scss';

interface AppProps {
  configStore: ConfigStore;
  experimentsStore: ExperimentsStore;
  redirectStore: RedirectStore;
  routingStore: RoutingStore;
  uiLoggerStore: UiLoggerStore;
  templatesStore: TemplatesStore;
  forYouPageStore: ForYouPageStore;
  headerAndFooterData?: HeaderFooterTypes.SSRData;
}

interface CategoryAndSubCategoryProps {
  category: string | 'search' | null;
  subCategory: string | null;
}

interface BiElementDataProps {
  clicked_element: string | 'other';
  clicked_element_identifier: string | 'NULL';
  galleryDocIndex: number;
  clickedTemplateHasVideo?: boolean;
}

const AppCmp = (props: AppProps) => {
  const {
    configStore,
    experimentsStore,
    headerAndFooterData,
    redirectStore,
    routingStore,
    uiLoggerStore,
    templatesStore,
    forYouPageStore,
  } = props;

  const scrollToTitleElementRef = React.useRef<HTMLDivElement>();
  const subTitleRef = React.useRef<HTMLDivElement>();
  const isShowForYouPage = isForYouPageRouteLoaded(
    routingStore.matchedRoute,
    forYouPageStore.isLoaded,
    templatesStore.isLoaded,
  );

  const getBiCategoryAndSubCategory = (element: HTMLElement): CategoryAndSubCategoryProps => {
    let category = element?.getAttribute('data-bi-element-category') ?? null;
    let subCategory = element?.getAttribute('data-bi-element-subcategory') ?? null;

    if (!category && !isShowForYouPage) {
      category =
        templatesStore.loadedFetchParams?.type === 'search' ? 'search' : templatesStore.loadedFetchParams?.categorySlug;
    }

    if (!subCategory && !isShowForYouPage) {
      subCategory = templatesStore.loadedFetchParams?.subCategorySlug;
    }
    return {
      category,
      subCategory,
    };
  };

  const getBiElementData = (target: HTMLElement): BiElementDataProps => {
    const galleryDocIndexElement = target.closest('[data-bi-gallery-doc-index]');
    const galleryDocIndex = Number(galleryDocIndexElement?.getAttribute('data-bi-gallery-doc-index'));

    const biElement = target.closest('[data-bi-element]');
    const clickedElement = biElement?.getAttribute('data-bi-element') ?? 'other';
    const clickedElementIdentifier = biElement?.getAttribute('data-bi-element-value') ?? 'NULL';

    const templateHasVideoElement = target.closest('[data-bi-template-has-video]');
    const templateHasVideoDataValue = templateHasVideoElement?.getAttribute('data-bi-template-has-video');
    const clickedTemplateHasVideo = templateHasVideoDataValue ? templateHasVideoDataValue === 'true' : undefined;

    return {
      clicked_element: clickedElement,
      clicked_element_identifier: clickedElementIdentifier,
      galleryDocIndex,
      clickedTemplateHasVideo,
    };
  };

  const headerAndFooterConfig: HeaderFooterTypes.Config = {
    hostPage: null,
    supportedLanguages: configStore.config.supportedLanguages,
    language: configStore.config.currentLanguage,
  };

  const scrollToTop = () => scrollToTitleElementRef.current.scrollIntoView();
  const focusOnSubTitle = () => subTitleRef.current && subTitleRef.current.focus();
  const handleBiElementClick = (event: MouseEvent): void => {
    const biElementData = getBiElementData(event.target as HTMLElement);
    const categoryAndSubCategory = getBiCategoryAndSubCategory(event.target as HTMLElement);

    uiLoggerStore.logClickElement({
      ...biElementData,
      ...categoryAndSubCategory,
    });
  };

  React.useEffect(() => {
    document.body.addEventListener('click', (event) => handleBiElementClick(event));
    return () => document.body.removeEventListener('click', handleBiElementClick);
  }, [handleBiElementClick]);

  const isForYouExperiment = experimentsStore.is('specs.funnel.TemplatesGalleryForYouPage', 'true');

  const enableWixHeaderAndFooter = experimentsStore.is(
    'specs.funnel.TemplatesGalleryDisableWixHeaderAndFooter',
    'false',
  );
  return redirectStore.redirect ? (
    <Redirect to={redirectStore.redirect.location} />
  ) : (
    <ScrollToTopContext.Provider value={{ scrollToTop }}>
      <div data-hook="app">
        <HtmlHead />
        {enableWixHeaderAndFooter && (
          <WixHeader ssr={headerAndFooterData} config={headerAndFooterConfig} withHeaderStartHeight={true} />
        )}
        <DealerBanner bannerPosition="aef4fb1f-f954-48e4-a31d-b4a49ca84955" />
        <div ref={scrollToTitleElementRef} />
        <HeaderTitle dataHook={appDataHooks.headerTitle()} focusOnSubTitle={focusOnSubTitle} />
        {isForYouExperiment ? (
          <HeaderMenuPanelForYou dataHook={appDataHooks.headerMenuPanel()} focusOnSubTitle={focusOnSubTitle} />
        ) : (
          <HeaderMenuPanel dataHook={appDataHooks.headerMenuPanel()} focusOnSubTitle={focusOnSubTitle} />
        )}
        <div className={styles.body}>
          {isShowForYouPage ? (
            <ForYouPage dataHook={appDataHooks.forYouPage()} />
          ) : (
            <TemplatesPage dataHook={appDataHooks.templatesPage()} />
          )}
        </div>
        <DealerBanner bannerPosition="33ff7d65-a3c3-4c88-bfca-e0924bb5cfac" />
        {enableWixHeaderAndFooter && <WixFooter />}
      </div>
    </ScrollToTopContext.Provider>
  );
};

export const App = injectV2(
  'configStore',
  'experimentsStore',
  'redirectStore',
  'routingStore',
  'templatesStore',
  'forYouPageStore',
  'uiLoggerStore',
)(AppCmp);
